import { PlusOutlined } from "@ant-design/icons";
import { Box, HStack, VStack } from "@chakra-ui/react";
import { Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Switch, TimePicker } from "antd";
import CustomerSelector from "components/CustomerSelector";
import { Roles } from "context/auth";
import usePackageTypeUpdate from "hooks/usePackageTypeUpdate";
import { ApplicationUser } from "models/applicationUsers";
import { DayOfWeek } from "models/common";
import { PackageTypeWeight, PackageTypeWeightNames } from "models/packages";
import { DisplayDateFormat, DisplayTimeFormat, ServerTimeFormat } from "models/websiteSettings";
import moment from "moment";
import { FC, useState } from "react";
import { dollarsToCents } from "util/helpers";
import { BannedAuctionWindowHours, Kind, bannerOrdinalValidator, feeValidator, toNullableNumber, windowValidator } from "./helpers";

interface NewPackageTypeForm {
    displayName: string;
    internalName: string;
    usesAmounts: boolean;
    leadTimeInDays: number | null;
    estUploadDate: moment.Moment | null;
    eventDayOfWeek: DayOfWeek | null;
    processingFee: string | null;
    processingFeeInCents: number;
    bannerOrdinal: number | null;
    sortingWeight: PackageTypeWeight;
    supportSilentAccounts: boolean;
    abbreviation: string;
    allowMultipleOwners: boolean;
    auctionWindow: [moment.Moment, moment.Moment] | null;
    enableKickbacks: boolean;
    kickbackCustomer: ApplicationUser | null;
    kickbackPercent: number | null;
}

const NewModal: FC<{
    onSuccess: () => Promise<any>
}> = ({ onSuccess }) => {
    const { createPackageType } = usePackageTypeUpdate();
    const [visible, setVisible] = useState<boolean>(false);
    const [kind, setKind] = useState<Kind>(Kind.OneTimeEvent);
    const [working, setWorking] = useState<boolean>(false);
    const [form] = Form.useForm<NewPackageTypeForm>();

    const {
        enableKickbacks,
        supportSilentAccounts
    } = Form.useWatch([], form) ?? {}

    const reset = () => {
        form.resetFields();
        setKind(1);
        setVisible(false);
    }

    const submit = () => {
        form.validateFields().then(async (data) => {
            setWorking(true);

            try {
                const [windowStart, windowEnd] = data.auctionWindow ?? [null, null];
                await createPackageType({
                    ...data,
                    estimatedUploadDate: data.estUploadDate
                        ? data.estUploadDate.toISOString()
                        : null,
                    eventDayOfWeek: (!data.leadTimeInDays && !data.estUploadDate)
                        ? data.eventDayOfWeek
                        : null,
                    auctionStartTime: windowStart
                        ? windowStart.format(ServerTimeFormat)
                        : null,
                    auctionEndTime: windowEnd
                        ? windowEnd.format(ServerTimeFormat)
                        : null,
                    kickbackRecipientId: data.kickbackCustomer?.id ?? null,
                    kickbackPercentInPoints: data.kickbackPercent
                });
                await onSuccess();
                setVisible(false);
            } finally {
                setWorking(false);
            }
        });
    }

    return <>
        <Button icon={<PlusOutlined />} type="primary" title="Add New Package Type" onClick={() => setVisible(true)} />
        <Modal
            open={visible}
            destroyOnClose
            closable={false}
            keyboard={false}
            maskClosable={false}
            onCancel={reset}
            onOk={submit}
            confirmLoading={working}
            cancelButtonProps={{ disabled: working }}
            okText={working ? "Saving..." : "Create"}
            title="Create New Package Type"
        >
            <Form
                form={form}
                preserve={false}
                layout="vertical"
                initialValues={{ isEvent: true, usesAmounts: true, kind: Kind.OneTimeEvent, eventDayOfWeek: DayOfWeek.Sunday, sortingWeight: PackageTypeWeight.Standard }}
            >
                <HStack display='flex'>
                    <Form.Item
                        label="Internal Name"
                        name="internalName"
                        rules={[{ required: true, message: "Internal Name is required!" }]}
                        style={{ marginBottom: 0, width: '50%' }}>
                        <Input placeholder="Shown to dcsports87 staff" />
                    </Form.Item>

                    <Form.Item
                        label="Display Name"
                        name="displayName"
                        rules={[{ required: true, message: "Display Name is required!" }]}
                        style={{ width: '50%' }}>
                        <Input placeholder="Shown to customers" />
                    </Form.Item>
                </HStack>

                <HStack display='flex' mt={5} mb={5} alignItems='start'>
                    <VStack m={0} p={0} w='50%'>
                        <Box w='100%'>
                            <Form.Item label="Abbreviation" name="abbreviation" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                <Input placeholder="For package QR codes" />
                            </Form.Item>
                        </Box>
                        <Box display='flex' justifyContent='start' alignItems='center' m='15px 0'>
                            <label style={{ marginRight: '10px' }}>Uses Package Amounts?</label>
                            <Form.Item
                                name="usesAmounts"
                                style={{ marginBottom: 0 }}
                                valuePropName="checked">
                                <Switch
                                    checkedChildren="Yes"
                                    unCheckedChildren="No" />
                            </Form.Item>
                        </Box>
                    </VStack>

                    <Box display='flex' w='50%'>
                        <Form.Item
                            label="What kind of package type is it?"
                            name="kind"
                            style={{ width: '100%' }}
                            rules={[{ required: true, message: "This is required!" }]}>
                            <Select
                                onChange={setKind}
                                options={[
                                    { label: 'It\'s based on a lead time', value: Kind.LeadTime },
                                    { label: 'It\'s for a one-time event', value: Kind.OneTimeEvent },
                                    { label: 'It\'s a recurring event', value: Kind.WeeklyEvent },
                                ]}
                            />
                        </Form.Item>
                    </Box>
                </HStack>

                <HStack display='flex'>
                    <Form.Item
                        label="Banner Order #"
                        name="bannerOrdinal"
                        style={{ marginBottom: 0, width: '33%' }}
                        rules={[{ transform: toNullableNumber, validator: (r, v) => bannerOrdinalValidator(form, r, v) }]}>
                        <Input placeholder="Not Shown" type="number" />
                    </Form.Item>

                    <Form.Item
                        label="Processing Fee"
                        name="processingFee"
                        style={{ width: '33%' }}
                        rules={[{ transform: dollarsToCents, validator: (r, v) => feeValidator(form, r, v) }]}>
                        <Input prefix="$" placeholder="No Fee" />
                    </Form.Item>

                    {kind === Kind.LeadTime && <Form.Item
                        label="Lead Time in Days"
                        name="leadTimeInDays"
                        style={{ width: '33%' }}
                        rules={[{ required: true, message: "Lead Time is required!" }]}>
                        <Input type="number" />
                    </Form.Item>}

                    {kind === Kind.OneTimeEvent && <Form.Item
                        label="Est. Upload Date"
                        name="estUploadDate"
                        style={{ width: '33%' }}
                        rules={[{ required: true, message: "Date is required!" }]}>
                        <DatePicker
                            format={DisplayDateFormat}
                            disabledDate={(date: moment.Moment) => date.isBefore(moment())} />
                    </Form.Item>}

                    {kind === Kind.WeeklyEvent && <Form.Item
                        label="Event Starts On"
                        name="eventDayOfWeek"
                        style={{ width: '33%' }}
                        rules={[{ required: true, message: "Day is required!" }]}>
                        <Select
                            options={[
                                { label: 'Sundays', value: DayOfWeek.Sunday },
                                { label: 'Mondays', value: DayOfWeek.Monday },
                                { label: 'Tuesdays', value: DayOfWeek.Tuesday },
                                { label: 'Wednesdays', value: DayOfWeek.Wednesday },
                                { label: 'Thursdays', value: DayOfWeek.Thursday },
                                { label: 'Fridays', value: DayOfWeek.Friday },
                                { label: 'Saturdays', value: DayOfWeek.Saturday },
                            ]}
                        />
                    </Form.Item>}
                </HStack>

                <Form.Item
                    label="Sorting Weight (For Incoming Packages)"
                    name="sortingWeight"
                    style={{ marginTop: '15px' }}>
                    <Select
                        options={[
                            { label: PackageTypeWeightNames[PackageTypeWeight.Premium], value: PackageTypeWeight.Premium },
                            { label: PackageTypeWeightNames[PackageTypeWeight.Priority], value: PackageTypeWeight.Priority },
                            { label: PackageTypeWeightNames[PackageTypeWeight.Standard], value: PackageTypeWeight.Standard }
                        ]}
                    />
                </Form.Item>

                <Form.Item
                    label="Auction Start Time Window"
                    name="auctionWindow"
                    extra="Define an optional window of time that auctions will begin for cards in packages of this type (Eastern Time)"
                    rules={[{
                        validator: (r, v) => windowValidator(r, v)
                    }]}
                >
                    <TimePicker.RangePicker
                        use12Hours
                        allowClear
                        format={DisplayTimeFormat}
                        style={{ width: '100%' }}
                        disabledTime={() => ({
                            disabledHours: () => BannedAuctionWindowHours
                        })} />
                </Form.Item>

                <Space>
                    <Form.Item
                        label="Kickbacks?"
                        name="enableKickbacks"
                        valuePropName="checked">
                        <Switch
                            disabled={supportSilentAccounts}
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                            onChange={() => form.setFieldsValue({ supportSilentAccounts: false })} />
                    </Form.Item>

                    <Form.Item
                        label="Recipient"
                        name="kickbackCustomer"
                        rules={[{ required: enableKickbacks, message: "Recipient is required!" }]}>
                        <CustomerSelector
                            disabled={!enableKickbacks}
                            roles={[Roles.User]}
                            onSelected={(kickbackCustomer) => form.setFieldsValue({ kickbackCustomer })}
                            onClear={() => form.setFieldsValue({ kickbackCustomer: undefined })}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Amount per Sale"
                        name="kickbackPercent"
                        rules={[{ required: enableKickbacks, message: "Percent is required!" }]}>
                        <InputNumber disabled={!enableKickbacks}
                            min={0.001}
                            max={3}
                            precision={3}
                            addonAfter="%"
                            controls={false} />
                    </Form.Item>
                </Space>

                <Space>
                    <Form.Item
                        label="Restrict to silent accounts?"
                        help="Restrict ownership of packages and the cards therein to silent accounts based on an account owner, typically a breaker. Cannot be used with kickbacks."
                        name="supportSilentAccounts"
                        style={{ marginBottom: 0 }}
                        valuePropName="checked">
                        <Switch
                            disabled={enableKickbacks}
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                            onChange={() => form.setFieldsValue({ enableKickbacks: false })} />
                    </Form.Item>

                    <Form.Item
                        label="Allow Multiple Owners?"
                        help="Allow packages and the cards therein to be assigned to multiple silent or consignment accounts, each owner getting an even share of any sales"
                        name="allowMultipleOwners"
                        style={{ marginBottom: 0 }}
                        valuePropName="checked">
                        <Switch
                            checkedChildren="Yes"
                            unCheckedChildren="No" />
                    </Form.Item>
                </Space>
                <Form.Item
                    hidden
                    name="processingFeeInCents">
                    <Input />
                </Form.Item>
            </Form>
        </Modal >
    </>;
};

export default NewModal;